import { AppHealth, HealthStatus, ServiceBusEntity } from './models';
import { Tone } from './tone';

// One count in a summary, such as "3 unhealthy"; the key says what it counts so other views can pick it out.
export interface SummaryPart {
  key:
    | 'unhealthy'
    | 'degraded'
    | 'unreadable'
    | 'nodata'
    | 'healthy'
    | 'dead'
    | 'deadMessages'
    | 'clean';
  label: string;
  count: number;
  tone: Tone;
}

export interface Summary {
  tone: 'ok' | 'warn' | 'bad';
  headline: string;
  // Only what is not zero.
  parts: SummaryPart[];
}

// The overall picture over every role, whatever the filter: is anything wrong?
export function summarizeApps(apps: AppHealth[]): Summary {
  const count = (status: HealthStatus): number =>
    apps.filter((app) => app.status === status).length;
  const unhealthy = count('Unhealthy');
  const degraded = count('Degraded');
  const unreadable = count('Error') + count('AccessDenied');
  return {
    tone: unhealthy > 0 ? 'bad' : degraded > 0 || unreadable > 0 ? 'warn' : 'ok',
    headline:
      unhealthy > 0
        ? 'Needs attention'
        : degraded > 0
          ? 'Some degradation'
          : unreadable > 0
            ? 'Some cards could not be read'
            : 'All systems healthy',
    parts: nonZero([
      { key: 'unhealthy', label: 'unhealthy', count: unhealthy, tone: 'bad' },
      { key: 'degraded', label: 'degraded', count: degraded, tone: 'warn' },
      { key: 'unreadable', label: 'error', count: count('Error'), tone: 'warn' },
      { key: 'unreadable', label: 'no access', count: count('AccessDenied'), tone: 'warn' },
      { key: 'nodata', label: 'no data', count: count('NoData'), tone: 'neutral' },
      { key: 'healthy', label: 'healthy', count: count('Healthy'), tone: 'ok' },
    ]),
  };
}

export function summarizeServiceBus(entities: ServiceBusEntity[]): Summary {
  const dead = entities.filter((entity) => entity.status === 'DeadLettered');
  const clean = entities.filter((entity) => entity.status === 'Clean').length;
  const unreadable = entities.length - dead.length - clean;
  const messages = dead.reduce((sum, entity) => sum + entity.deadLetterMessageCount, 0);
  return {
    tone: dead.length > 0 ? 'bad' : unreadable > 0 ? 'warn' : 'ok',
    headline:
      dead.length > 0
        ? 'Dead letters need attention'
        : unreadable > 0
          ? 'Some queues and topics could not be read'
          : 'No dead letters',
    parts: nonZero([
      { key: 'dead', label: 'with dead letters', count: dead.length, tone: 'bad' },
      {
        key: 'deadMessages',
        label: messages === 1 ? 'dead-lettered message' : 'dead-lettered messages',
        count: messages,
        tone: 'bad',
      },
      { key: 'unreadable', label: 'could not be read', count: unreadable, tone: 'warn' },
      { key: 'clean', label: 'without dead letters', count: clean, tone: 'ok' },
    ]),
  };
}

function nonZero(parts: SummaryPart[]): SummaryPart[] {
  return parts.filter((part) => part.count > 0);
}

// Both kinds of data in one sentence: the worst tone wins, and the counts that matter are added up.
export function summarizeOverall(apps: Summary | null, serviceBus: Summary | null): Summary | null {
  const known = [apps, serviceBus].filter((summary): summary is Summary => summary !== null);
  if (known.length === 0) {
    return null;
  }
  const total = (key: SummaryPart['key'], from: Summary | null): number =>
    from?.parts.filter((part) => part.key === key).reduce((sum, part) => sum + part.count, 0) ?? 0;
  const unhealthy = total('unhealthy', apps);
  const degraded = total('degraded', apps);
  const dead = total('dead', serviceBus);
  const unreadable = total('unreadable', apps) + total('unreadable', serviceBus);
  const tone = known.some((s) => s.tone === 'bad')
    ? 'bad'
    : known.some((s) => s.tone === 'warn')
      ? 'warn'
      : 'ok';
  return {
    tone,
    headline:
      tone === 'bad' ? 'Needs attention' : tone === 'warn' ? 'Worth a look' : 'All systems healthy',
    parts: nonZero([
      {
        key: 'unhealthy',
        label: unhealthy === 1 ? 'unhealthy role' : 'unhealthy roles',
        count: unhealthy,
        tone: 'bad',
      },
      {
        key: 'dead',
        label:
          dead === 1 ? 'queue or topic with dead letters' : 'queues and topics with dead letters',
        count: dead,
        tone: 'bad',
      },
      {
        key: 'degraded',
        label: degraded === 1 ? 'degraded role' : 'degraded roles',
        count: degraded,
        tone: 'warn',
      },
      { key: 'unreadable', label: 'could not be read', count: unreadable, tone: 'warn' },
      {
        key: 'healthy',
        label: 'healthy',
        count: total('healthy', apps) + total('clean', serviceBus),
        tone: 'ok',
      },
    ]),
  };
}

// What one source (Application Insights, Service Bus, later the clusters) shows as a tile on the overview.
export interface OverviewSource {
  id: string;
  title: string;
  // Where its details are.
  route: string;
  state: 'loading' | 'ready' | 'unavailable' | 'empty';
  tone: Tone;
  headline: string;
  // What is watched, such as "roles" or "queues and topics".
  noun: string;
  total: number;
  parts: SummaryPart[];
  updated: Date | null;
  // Set when the latest read failed; with data it says the tile may be out of date.
  problem: string | null;
}

export interface SourceInput {
  loading: boolean;
  error: string | null;
  updated: Date | null;
}

function source(
  id: string,
  title: string,
  route: string,
  noun: string,
  total: number,
  summary: Summary,
  input: SourceInput,
  emptyMessage: string,
): OverviewSource {
  const base = { id, title, route, noun, total, updated: input.updated, problem: input.error };
  if (total > 0) {
    return {
      ...base,
      state: 'ready',
      tone: summary.tone,
      headline: summary.headline,
      parts: summary.parts,
    };
  }
  if (input.loading && input.error === null) {
    return { ...base, state: 'loading', tone: 'neutral', headline: 'Loading', parts: [] };
  }
  return input.error === null
    ? { ...base, state: 'empty', tone: 'neutral', headline: emptyMessage, parts: [] }
    : { ...base, state: 'unavailable', tone: 'warn', headline: 'Could not be loaded', parts: [] };
}

export function applicationInsightsSource(apps: AppHealth[], input: SourceInput): OverviewSource {
  return source(
    'application-insights',
    'Application Insights',
    '/application-insights',
    apps.length === 1 ? 'role' : 'roles',
    apps.length,
    summarizeApps(apps),
    input,
    'No apps are configured',
  );
}

export function serviceBusSource(entities: ServiceBusEntity[], input: SourceInput): OverviewSource {
  return source(
    'service-bus',
    'Service Bus',
    '/service-bus',
    entities.length === 1 ? 'queue or topic' : 'queues and topics',
    entities.length,
    summarizeServiceBus(entities),
    input,
    'No queues or topics are configured',
  );
}

// One thing that is wrong right now, with where to look at it.
export interface AttentionItem {
  id: string;
  sourceTitle: string;
  title: string;
  subtitle: string | null;
  detail: string;
  tone: Tone;
  route: string;
  // What to search for on the details page to find it.
  search: string;
}

const TONE_RANK: Record<Tone, number> = { bad: 0, warn: 1, ok: 2, neutral: 3 };

// Unhealthy roles and queues with dead letters first, then degraded roles; the worst of each kind leads.
export function attentionItems(apps: AppHealth[], entities: ServiceBusEntity[]): AttentionItem[] {
  const fromApps = apps
    .filter((app) => app.status === 'Unhealthy' || app.status === 'Degraded')
    .map<AttentionItem>((app) => ({
      id: `app|${app.appId}|${app.roleName ?? ''}`,
      sourceTitle: 'Application Insights',
      title: app.name ?? app.appId,
      subtitle: app.roleName,
      detail: app.statusReason ?? (app.status === 'Unhealthy' ? 'Unhealthy' : 'Degraded'),
      tone: app.status === 'Unhealthy' ? 'bad' : 'warn',
      route: '/application-insights',
      search: app.name ?? app.appId,
    }));
  const fromServiceBus = entities
    .filter((entity) => entity.status === 'DeadLettered')
    .sort((a, b) => b.deadLetterMessageCount - a.deadLetterMessageCount)
    .map<AttentionItem>((entity) => ({
      id: `sb|${entity.namespace}|${entity.kind}|${entity.name}`,
      sourceTitle: 'Service Bus',
      title: entity.name,
      subtitle: `${entity.kind} in ${entity.namespace}`,
      detail: `${entity.deadLetterMessageCount.toLocaleString('en')} dead-lettered ${entity.deadLetterMessageCount === 1 ? 'message' : 'messages'}`,
      tone: 'bad',
      route: '/service-bus',
      search: entity.name,
    }));
  // The sort is stable, so within a tone the apps keep their order and the queues theirs.
  return [...fromApps, ...fromServiceBus].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);
}
